// ZoneFormDrawer — create / edit a zone (name + default), the drawer container of
// the form-surface taxonomy (ADR-038). "Edit zone" lives in the header (the thin
// entity has no editable section); create + edit share this one drawer. On create
// it lands on the new zone's detail; on edit it applies only the changed fields
// (name via PATCH, default via the dedicated set-default action — you don't
// un-default a zone, you set another as default). The edit rides the row stamp
// (ADR-084): the shell seeds from the zone as it is at each open, the rename
// carries the stamp and goes first (set-default is an act, which moves it), and a
// stale save shows the line with Load the latest.

import { useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Controller } from 'react-hook-form';
import { z } from 'zod';
import { DrawerForm } from '../../../components/patterns/forms/DrawerForm.js';
import { readLatest } from '../../../components/patterns/forms/readLatest.js';
import { useFormShell } from '../../../components/patterns/forms/useFormShell.js';
import { Input } from '../../../components/ui/Input.js';
import { Label } from '../../../components/ui/Label.js';
import { Toggle } from '../../../components/ui/Toggle.js';
import { useCreateZone, useSetZoneDefault, useUpdateZone } from '../../../hooks/useZoneMutations.js';
import { fetchZone, zonesKeys } from '../../../hooks/useZones.js';

// Local form schema (ADR-038 §2): is_default is a required boolean here (the
// toggle's value), unlike the wire CreateZoneRequest where it defaults. name
// .max(32) matches zones.name varchar(32).
const zoneFormSchema = z.object({
  name: z.string().trim().min(1).max(32),
  is_default: z.boolean(),
});
type ZoneFormValues = z.infer<typeof zoneFormSchema>;

export interface ZoneFormTarget {
  id: number;
  name: string;
  is_default: boolean;
  is_disabled: boolean;
  row_version: number;
}

export function ZoneFormDrawer({
  projectId,
  zone,
  open,
  onClose,
  onCreated,
}: {
  projectId: string;
  /** Omit for create; provide the zone for edit. */
  zone?: ZoneFormTarget;
  open: boolean;
  onClose: () => void;
  /** Create only: called with the new zone's id after a successful create — the list
   *  opens its peek (zones are peek-only, ADR-051; there is no detail page to land on). */
  onCreated?: (id: number) => void;
}) {
  const { t } = useTranslation('zones');
  const isEdit = Boolean(zone);
  const createZone = useCreateZone(projectId);
  const updateZone = useUpdateZone(projectId);
  const setDefault = useSetZoneDefault(projectId);
  const createdId = useRef<number | null>(null);
  const qc = useQueryClient();

  const shell = useFormShell<ZoneFormValues, ZoneFormTarget>({
    schema: zoneFormSchema,
    row: zone ?? null,
    values: (r) => ({ name: r?.name ?? '', is_default: r?.is_default ?? false }),
    reload: zone ? () => readLatest(qc, zonesKeys.detail(projectId, zone.id), () => fetchZone(projectId, zone.id, true)) : undefined,
    onSubmit: async (v, { stamp, changed }) => {
      if (zone) {
        // Only what changed since the drawer opened — the rename with the stamp, first.
        if ('name' in changed) await updateZone.mutateAsync({ zoneId: zone.id, body: { row_version: stamp, name: v.name } });
        // Default is set, never unset — promoting a zone clears the prior; you
        // don't "remove" a default (one always exists). Only act on false→true.
        if ('is_default' in changed && v.is_default) await setDefault.mutateAsync(zone.id);
      } else {
        const r = await createZone.mutateAsync({ name: v.name, is_default: v.is_default });
        createdId.current = r.id;
      }
    },
    onSuccess: () => {
      const newId = createdId.current;
      createdId.current = null;
      onClose();
      if (newId) onCreated?.(newId);
    },
  });

  return (
    <DrawerForm
      shell={shell}
      open={open}
      onClose={onClose}
      title={isEdit ? t('form.editTitle') : t('form.createTitle')}
      description={isEdit ? undefined : t('form.createHint')}
      submitLabel={isEdit ? t('form.save') : t('form.create')}
      autoFocusField="name"
    >
      <div>
        <Label htmlFor="zone-name" required>
          {t('form.name')}
        </Label>
        <Input
          id="zone-name"
          {...shell.form.register('name')}
          error={shell.form.formState.errors.name ? ' ' : undefined}
          placeholder={t('form.namePlaceholder')}
        />
      </div>
      {/* A switched-off zone is never the default (the action door's rule too) — the choice is not offered. */}
      {!zone?.is_disabled && (
        <Controller
          control={shell.form.control}
          name="is_default"
          render={({ field }) => (
            <Toggle checked={field.value} onChange={field.onChange} label={t('form.setDefault')} />
          )}
        />
      )}
    </DrawerForm>
  );
}
